import { BaseService } from '~/services/base/base.service';
import { SemanticCacheRepository } from './semantic-cache.repository';
import { ModelSemanticCacheRecord } from './dto/semantic-cache.dto';

export class SemanticCacheService extends BaseService {
  private repo = new SemanticCacheRepository(this.api, this.configService);

  async categories() {
    const { $functions } = useNuxtApp();
    try {
      const res = await this.repo.categories();
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        return (resData.data || []) as Array<{ category: string; count: number }>;
      }
    } catch (err: any) {
      $functions.notify.error({ text: err.message, isError: true });
    }
    return [];
  }

  async list(params: any) {
    const result: any = { data: [], totalItems: 0 };
    const { $functions } = useNuxtApp();
    try {
      const res = await this.repo.list(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        result.data = (resData.data || []).map(
          (e: any) => new ModelSemanticCacheRecord(e),
        );
        result.totalItems = resData.totalItems;
      }
    } catch (err: any) {
      $functions.notify.error({ text: err.message, isError: true });
    }
    return result;
  }

  async getById(id: string) {
    const { $functions } = useNuxtApp();
    try {
      const res = await this.repo.getById(id);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        return new ModelSemanticCacheRecord(resData.data);
      }
    } catch (err: any) {
      $functions.notify.error({ text: err.message, isError: true });
    }
    return undefined;
  }

  async create(data: any) {
    const { $functions } = useNuxtApp();
    try {
      const res = await this.repo.create(data);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        return new ModelSemanticCacheRecord(resData.data);
      }
    } catch (err: any) {
      $functions.notify.error({ text: err.message, isError: true });
    }
    return undefined;
  }

  async update(id: string, data: any) {
    const { $functions } = useNuxtApp();
    try {
      const res = await this.repo.update(id, data);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        return new ModelSemanticCacheRecord(resData.data);
      }
    } catch (err: any) {
      $functions.notify.error({ text: err.message, isError: true });
    }
    return undefined;
  }

  async remove(id: string) {
    const { $functions } = useNuxtApp();
    try {
      const res = await this.repo.remove(id);
      return $functions.isValidResponse(res.data.value);
    } catch (err: any) {
      $functions.notify.error({ text: err.message, isError: true });
      return false;
    }
  }

  async importCsv(file: File) {
    const { $functions } = useNuxtApp();
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await this.repo.importCsv(formData);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        return resData.data; // { created, updated, unchanged }
      }
    } catch (err: any) {
      $functions.notify.error({ text: err.message, isError: true });
    }
    return undefined;
  }

  async approve(id: string) {
    const { $functions } = useNuxtApp();
    try {
      const res = await this.repo.approve(id);
      return $functions.isValidResponse(res.data.value);
    } catch (err: any) {
      $functions.notify.error({ text: err.message, isError: true });
      return false;
    }
  }

  async approveAll(category?: string) {
    const { $functions } = useNuxtApp();
    try {
      const res = await this.repo.approveAll(category);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        return resData.data; // { queued }
      }
    } catch (err: any) {
      $functions.notify.error({ text: err.message, isError: true });
    }
    return undefined;
  }

  async rewrite(question: string, lang: string) {
    const { $functions } = useNuxtApp();
    try {
      const res = await this.repo.rewrite({ question, lang });
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        return resData.data; // { answer, inputToken, outputToken }
      }
    } catch (err: any) {
      $functions.notify.error({ text: err.message, isError: true });
    }
    return undefined;
  }

  async test(query: string) {
    const { $functions } = useNuxtApp();
    try {
      const res = await this.repo.test({ query });
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        return resData.data as any[]; // hits[]
      }
    } catch (err: any) {
      $functions.notify.error({ text: err.message, isError: true });
    }
    return [];
  }
}
